import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Bell, CheckCheck, Settings } from "lucide-react";
import { useState } from "react";
import type { Notification } from "@estelle/domain";

import { LoadFailed } from "@/components/shell/EmptyState";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { notificationLink } from "@/components/notifications/links";
import {
  UNREAD_COUNT_KEY,
  useMarkAllRead,
  useMarkRead,
  useNotificationList,
  useUnreadCount,
} from "@/components/notifications/queries";
import { ago } from "@/components/notifications/time";
import { cn } from "@/lib/utils";

/**
 * The bell: an unread count in the header, and a panel of what happened
 * (E6, #446, slice 1).
 *
 * Rendered once by `PageContainer`, the way the Feedback button is, so every
 * screen has it. The count refreshes on window focus and about once a minute;
 * marking something read refreshes the badge and the panel together, with no
 * reload (`queries.ts`).
 *
 * A row opens the record it points to and marks itself read. Opening is
 * ordinary navigation — the destination checks permission as it always does;
 * the bell only ever lists what `notify()` decided the person may see.
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const unread = useUnreadCount();
  const count = unread.data ?? 0;

  const label =
    count === 0 ? "Notifications" : `Notifications, ${String(count)} unread`;

  /*
    Opening the panel reads the list afresh, so it also reads the badge afresh:
    the count only refreshes on focus and about once a minute, and a badge that
    says nothing above a panel that lists three unread rows is a bell that
    cannot be trusted.
  */
  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) {
      void queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={label}
          title="Notifications"
          className="relative"
        >
          <Bell />
          {count > 0 ? (
            <span
              aria-hidden
              data-testid="notification-count"
              className="bg-primary text-primary-foreground absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none font-semibold tabular-nums"
            >
              {count > 99 ? "99+" : count}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="w-[min(24rem,calc(100vw-1.5rem))] p-0"
        aria-label="Notifications"
      >
        <Panel
          open={open}
          unreadCount={count}
          onNavigate={() => {
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function Panel({
  open,
  unreadCount,
  onNavigate,
}: {
  open: boolean;
  unreadCount: number;
  onNavigate: () => void;
}) {
  const navigate = useNavigate();
  const list = useNotificationList(open);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const items = list.data?.pages.flatMap((page) => page.items) ?? [];

  function openNotification(notification: Notification) {
    if (notification.readAt === null) markRead.mutate(notification.id);
    const link = notificationLink(notification);
    onNavigate();
    void navigate({ to: link.to, search: link.search ?? {} });
  }

  return (
    <div className="flex max-h-[min(32rem,75svh)] flex-col">
      <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
        <h2 className="text-sm font-semibold">Notifications</h2>
        <Button
          size="xs"
          variant="ghost"
          disabled={unreadCount === 0 || markAllRead.isPending}
          onClick={() => {
            markAllRead.mutate();
          }}
        >
          <CheckCheck />
          Mark all read
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {list.isPending ? (
          <div className="space-y-3 p-3" aria-hidden>
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="space-y-1.5">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ))}
          </div>
        ) : list.isError ? (
          <LoadFailed
            message={list.error.message}
            onRetry={() => {
              void list.refetch();
            }}
          />
        ) : items.length === 0 ? (
          <p className="text-muted-foreground px-3 py-8 text-center text-sm">
            Nothing yet. You will see it here when a task is assigned to you,
            you are mentioned, or something changes on a task you follow.
          </p>
        ) : (
          <ul className="divide-y">
            {items.map((notification) => (
              <li key={notification.id}>
                <NotificationRow
                  notification={notification}
                  onOpen={openNotification}
                />
              </li>
            ))}
          </ul>
        )}

        {list.hasNextPage ? (
          <div className="border-t p-2 text-center">
            <Button
              size="xs"
              variant="ghost"
              disabled={list.isFetchingNextPage}
              onClick={() => {
                void list.fetchNextPage();
              }}
            >
              {list.isFetchingNextPage ? "Loading…" : "Show older"}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="border-t p-1.5">
        <Button
          size="xs"
          variant="ghost"
          className="w-full justify-start"
          onClick={() => {
            onNavigate();
            void navigate({ to: "/account/notifications" });
          }}
        >
          <Settings />
          Notification settings
        </Button>
      </div>
    </div>
  );
}

function NotificationRow({
  notification,
  onOpen,
}: {
  notification: Notification;
  onOpen: (notification: Notification) => void;
}) {
  const unread = notification.readAt === null;
  return (
    <button
      type="button"
      onClick={() => {
        onOpen(notification);
      }}
      className="hover:bg-accent focus-visible:bg-accent flex w-full items-start gap-2.5 px-3 py-2.5 text-left outline-none"
    >
      <span
        aria-hidden
        className={cn(
          "mt-1.5 size-2 shrink-0 rounded-full",
          unread ? "bg-primary" : "bg-transparent",
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            "block text-sm leading-snug",
            unread ? "font-medium" : "text-muted-foreground",
          )}
        >
          {notification.text}
          {unread ? <span className="sr-only"> (unread)</span> : null}
        </span>
        <span className="text-muted-foreground block truncate text-xs">
          {notification.actorName ? `${notification.actorName} · ` : ""}
          {ago(notification.createdAt)}
        </span>
      </span>
    </button>
  );
}
